import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getChatKey } from '../utils';

const POLL_INTERVAL = 30000;

export const FILTER_OPTIONS = {
  ALL: 'Todos',
  UNREAD: 'Não lidas',
};

/**
 * Lista de conversas: carga inicial + polling, busca e filtro combinados
 * (a busca e o filtro "Não lidas" são derivados via useMemo, então o
 * polling nunca sobrescreve o que o usuário digitou).
 */
export default function useChats(GetWhatsappChats, enabled) {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterOption, setFilterOption] = useState(FILTER_OPTIONS.ALL);

  const getChatsRef = useRef(GetWhatsappChats);
  getChatsRef.current = GetWhatsappChats;

  useEffect(() => {
    if (!enabled) return;

    let active = true;
    const fetchChats = () => {
      getChatsRef.current((result) => {
        if (!active) return;
        setChats(result?.data || []);
        setLoading(false);
      });
    };

    fetchChats();
    const intervalId = setInterval(fetchChats, POLL_INTERVAL);
    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [enabled]);

  const filteredChats = useMemo(() => {
    let filtered = chats;

    if (filterOption === FILTER_OPTIONS.UNREAD) {
      filtered = filtered.filter((chat) => Number(chat.unreadCount) > 0);
    }

    const query = searchText.trim().toLowerCase();
    if (query) {
      const queryDigits = query.replace(/\D/g, '');
      filtered = filtered.filter((chat) =>
        (chat.chatName || '').toLowerCase().includes(query) ||
        (queryDigits && (chat.bestPhone || chat.senderPhone || '').includes(queryDigits))
      );
    }

    return filtered;
  }, [chats, filterOption, searchText]);

  // Zera o contador local de não lidas de uma conversa (o servidor é
  // atualizado pelo endpoint mark-as-read chamado no useChatMessages)
  const markChatRead = useCallback((chatKey) => {
    setChats((prev) => prev.map((chat) =>
      getChatKey(chat) === chatKey ? { ...chat, unreadCount: 0 } : chat
    ));
  }, []);

  // Atualiza a prévia de uma conversa localmente (ex.: após enviar mensagem),
  // sem esperar o próximo ciclo do polling
  const updateChatPreview = useCallback((chatKey, patch) => {
    setChats((prev) => prev.map((chat) =>
      getChatKey(chat) === chatKey ? { ...chat, ...patch } : chat
    ));
  }, []);

  return {
    chats,
    filteredChats,
    loading,
    searchText,
    setSearchText,
    filterOption,
    setFilterOption,
    markChatRead,
    updateChatPreview,
  };
}
